import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
    FiX, FiDownload, FiMaximize2, FiMinimize2, FiZoomIn,
    FiZoomOut, FiRotateCw, FiFile, FiImage, FiFileText,
    FiAlertCircle, FiChevronLeft, FiChevronRight, FiMusic, FiVideo
} from 'react-icons/fi';
import { supabase } from '../../config/supabase';
import { useParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import PostalMime from 'postal-mime';
import { DxfViewer } from 'dxf-viewer';

const FileViewer = ({
    file,
    filename,
    originalName,
    onClose,
    files = [], // Array of files for navigation
    currentIndex = 0,
    onNavigate,
    jobId,
    company,
    fileType = 'initial' // Add fileType prop to determine which bucket to use
}) => {
    const { companySlug } = useParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [metadata, setMetadata] = useState(null);
    const [fileUrl, setFileUrl] = useState(null);
    const [isFullscreen, setIsFullscreen] = useState(true);
    const [zoom, setZoom] = useState(100);
    const [rotation, setRotation] = useState(0);
    const [fileContent, setFileContent] = useState(null); // For parsed text/html content
    const [parsedEmail, setParsedEmail] = useState(null); // For EML files
    const [excelData, setExcelData] = useState(null); // For Excel/CSV files
    const cadContainerRef = useRef(null); // For CAD viewer
    const [cadViewer, setCadViewer] = useState(null);
    const [currentFilePath, setCurrentFilePath] = useState(null);
    const docxContainerRef = useRef(null); // For Word document rendering

    useEffect(() => {
        fetchFileUrl();
    }, [filename]);

    useEffect(() => {
        setError(null);
        setLoading(true);
        setZoom(100);
        setRotation(0);
        setFileContent(null);
        setParsedEmail(null);
        setExcelData(null);
        // Clean up CAD viewer
        if (cadViewer) {
            cadViewer.Destroy();
            setCadViewer(null);
        }
    }, [filename]);

    const stripRtf = (rtf) => {
        let text = rtf;
        // Remove ignorable destination groups {\* ...}
        text = text.replace(/\{\\\*\\[^{}]*((?:\{[^{}]*\})[^{}]*)*\}/g, '');
        // Convert paragraph/line breaks to newlines before stripping
        text = text.replace(/\\par\b\s?/g, '\n');
        text = text.replace(/\\line\b\s?/g, '\n');
        text = text.replace(/\\tab\b\s?/g, '\t');
        // Decode \'xx hex-encoded characters (Windows-1252 common subset)
        text = text.replace(/\\'([0-9a-fA-F]{2})/g, (_, hex) => {
            try { return String.fromCharCode(parseInt(hex, 16)); } catch { return ''; }
        });
        // Remove all remaining control words and symbols
        text = text.replace(/\\[a-zA-Z]+[-]?\d*[ ]?/g, '');
        text = text.replace(/\\[^a-zA-Z]/g, '');
        // Remove braces
        text = text.replace(/[{}]/g, '');
        // Collapse excessive blank lines
        text = text.replace(/\n{3,}/g, '\n\n').trim();
        return text;
    };

    const parseFileContent = async (url, category, bucketName, filePath) => {
        try {
            if (category === 'text') {
                // Fetch and display plain text
                const response = await fetch(url);
                const text = await response.text();
                setFileContent(text);
            } else if (category === 'html') {
                // Fetch and display HTML
                const response = await fetch(url);
                const html = await response.text();
                setFileContent(html);
            } else if (category === 'word') {
                // Parse Word documents
                const extension = filePath.split('.').pop()?.toLowerCase();

                if (extension === 'doc') {
                    // Legacy .doc files cannot be parsed in the browser
                    setFileContent(null);
                } else {
                    // Use docx-preview for .docx files
                    const response = await fetch(url);
                    const arrayBuffer = await response.arrayBuffer();

                    // Store the arrayBuffer to render later with docx-preview
                    setFileContent(arrayBuffer);
                }
            } else if (category === 'excel') {
                // Parse Excel/CSV files
                const response = await fetch(url);
                const arrayBuffer = await response.arrayBuffer();
                const workbook = XLSX.read(arrayBuffer, { type: 'array', cellStyles: true, cellNF: true, cellDates: true });

                const sheets = workbook.SheetNames.reduce((acc, name) => {
                    acc[name] = XLSX.utils.sheet_to_html(workbook.Sheets[name], { editable: false });
                    return acc;
                }, {});
                setExcelData({
                    sheetNames: workbook.SheetNames,
                    sheets,
                    currentSheet: 0
                });
            } else if (category === 'eml') {
                // Parse EML files
                const response = await fetch(url);
                const emlText = await response.text();
                const parser = new PostalMime();
                const parsed = await parser.parse(emlText);
                setParsedEmail(parsed);
            } else if (category === 'rtf') {
                // Fetch RTF and strip control words to extract plain text
                const response = await fetch(url);
                const raw = await response.text();
                setFileContent(stripRtf(raw));
            } else if (category === 'cad') {
                // Parse CAD files with dxf-viewer
                // Will be rendered in useEffect when cadContainerRef is ready
                setFileContent('CAD_MODEL'); // Flag to trigger CAD rendering
            }
        } catch (err) {
            console.error('Error parsing file content:', err);

            if (category === 'excel') {
                setExcelData({ sheetNames: [], data: [['Error loading spreadsheet']], currentSheet: 0 });
            }
        }
    };

    const fetchFileUrl = async () => {
        try {
            setLoading(true);

            // 'external' is for files that aren't in the job-scoped storage
            // buckets at all — e.g. app_versions.files attachments, which
            // are just public URLs someone pasted in directly. There's no
            // company/jobId path to resolve, no signing, and no bucket to
            // list for a size lookup — the URL is used exactly as given.
            const isExternalFile = fileType === 'external';

            // Determine which bucket to use based on file_type
            const isDeliveryFile = fileType === 'delivery';
            const bucketName = isDeliveryFile ? 'delivery-uploads' : 'job-files';

            let filePath;
            let finalUrl;

            if (isExternalFile) {
                finalUrl = filename;
                filePath = filename;
            } else {
                // Handle different filename formats from database
                if (filename.startsWith('http://') || filename.startsWith('https://')) {
                    // If it's a full URL, extract the path after the bucket name
                    const urlParts = filename.split('/storage/v1/object/public/job-files/');
                    if (urlParts.length > 1) {
                        filePath = decodeURIComponent(urlParts[1]);
                    } else {
                        throw new Error('Invalid file URL format');
                    }
                } else if (filename.includes(`${company}/${jobId}`)) {
                    // Already has the full path structure
                    filePath = filename;
                } else if (filename.startsWith(`${company}/`)) {
                    // Has company but might be missing jobId
                    filePath = filename;
                } else {
                    // Just a filename or timestamp-based name, construct full path
                    filePath = `${company}/${jobId}/${filename}`;
                }

                if (isDeliveryFile) {
                    // delivery-uploads is a public bucket, get public URL directly
                    const { data: { publicUrl } } = supabase.storage
                        .from('delivery-uploads')
                        .getPublicUrl(filePath);

                    finalUrl = publicUrl;
                } else {
                    // job-files is private, create a signed URL
                    const { data: signedUrlData, error: signedUrlError } = await supabase.storage
                        .from('job-files')
                        .createSignedUrl(filePath, 3600);

                    if (signedUrlError) {
                        console.error('Signed URL error:', signedUrlError);

                        // Try alternative path if first attempt fails
                        const alternativePath = filename.startsWith(`${company}/`)
                            ? filename
                            : `${company}/${jobId}/${filename}`;

                        if (alternativePath !== filePath) {
                            const { data: retryData, error: retryError } = await supabase.storage
                                .from('job-files')
                                .createSignedUrl(alternativePath, 3600);

                            if (!retryError && retryData?.signedUrl) {
                                finalUrl = retryData.signedUrl;
                                setCurrentFilePath(alternativePath);
                            } else {
                                throw new Error('Failed to get file URL: ' + signedUrlError.message);
                            }
                        } else {
                            throw new Error('Failed to get file URL: ' + signedUrlError.message);
                        }
                    } else {
                        if (!signedUrlData?.signedUrl) {
                            throw new Error('Failed to get file URL');
                        }
                        finalUrl = signedUrlData.signedUrl;
                        setCurrentFilePath(filePath);
                    }
                }
            }


            setFileUrl(finalUrl);
            setCurrentFilePath(filePath);

            // Extract metadata from filename (strip any query string first —
            // external URLs aren't guaranteed to end in a bare extension)
            const extensionSource = (originalName || filename).split('?')[0];
            const extension = extensionSource.split('.').pop()?.toLowerCase() || '';
            const category = getFileCategory(extension);

            // Parse file content for previewable types
            await parseFileContent(finalUrl, category, bucketName, filePath);

            let size = 0;
            let sizeFormatted = 'Unknown size';

            if (!isExternalFile) {
                // Get file size from storage — extract the directory path
                // and filename for listing
                const pathParts = filePath.split('/');
                const fileName = pathParts[pathParts.length - 1];
                const directory = pathParts.slice(0, -1).join('/');

                const { data: fileList } = await supabase.storage
                    .from(bucketName)
                    .list(directory, {
                        search: fileName
                    });

                if (fileList && fileList.length > 0) {
                    const foundFile = fileList.find(f => f.name === fileName);
                    if (foundFile?.metadata?.size) {
                        size = foundFile.metadata.size;
                        sizeFormatted = formatFileSize(size);
                    }
                }
            }

            setMetadata({
                category,
                extension,
                size,
                sizeFormatted,
                name: originalName || filePath.split('/').pop()
            });

            setLoading(false);
        } catch (err) {
            console.error('Error loading file:', err);
            setError('Failed to load file: ' + err.message);
            setLoading(false);
        }
    };

    const getFileCategory = (extension) => {
        const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'];
        const pdfExtensions = ['pdf'];
        const cadExtensions = ['dxf', 'dwf']; // Only DXF and DWF supported
        const dwgExtensions = ['dwg']; // DWG - limited support, download recommended
        const textExtensions = ['txt', 'md', 'log', 'iv']; // .iv files shown as text
        const htmlExtensions = ['html', 'htm'];
        const wordExtensions = ['docx']; // Only .docx supported for preview
        const legacyDocExtensions = ['doc']; // Legacy .doc - download only
        const excelExtensions = ['xls', 'xlsx', 'csv'];
        const emlExtensions = ['eml'];
        const rtfExtensions = ['rtf'];
        const otherDocExtensions = ['ppt', 'pptx'];
        const audioExtensions = ['mp3', 'wav', 'ogg', 'm4a', 'aac', 'flac'];
        const videoExtensions = ['mp4', 'webm', 'ogg', 'mov', 'm4v'];

        if (imageExtensions.includes(extension)) return 'image';
        if (pdfExtensions.includes(extension)) return 'pdf';
        if (cadExtensions.includes(extension)) return 'cad';
        if (dwgExtensions.includes(extension)) return 'cad'; // Still try CAD viewer but with warning
        if (textExtensions.includes(extension)) return 'text';
        if (htmlExtensions.includes(extension)) return 'html';
        if (wordExtensions.includes(extension)) return 'word';
        if (legacyDocExtensions.includes(extension)) return 'document'; // Treat .doc as unsupported document
        if (excelExtensions.includes(extension)) return 'excel';
        if (emlExtensions.includes(extension)) return 'eml';
        if (rtfExtensions.includes(extension)) return 'rtf';
        if (otherDocExtensions.includes(extension)) return 'document';
        if (audioExtensions.includes(extension)) return 'audio';
        if (videoExtensions.includes(extension)) return 'video';
        return 'other';
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    const handleDownload = async () => {
        try {
            if (fileType === 'external') {
                // No storage bucket to download from — fetch the public URL
                // directly and save it as a blob so the browser downloads it
                // instead of navigating to it.
                const response = await fetch(filename);
                if (!response.ok) throw new Error('Failed to fetch file');

                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = originalName || filename.split('/').pop().split('?')[0];
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                return;
            }

            // Determine bucket based on file type
            const isDeliveryFile = fileType === 'delivery';
            const bucketName = isDeliveryFile ? 'delivery-uploads' : 'job-files';

            let filePath;

            // Handle different filename formats from database
            if (filename.startsWith('http://') || filename.startsWith('https://')) {
                // If it's a full URL, extract the path after the bucket name
                const urlParts = filename.split('/storage/v1/object/public/job-files/');
                if (urlParts.length > 1) {
                    filePath = decodeURIComponent(urlParts[1]);
                } else {
                    throw new Error('Invalid file URL format');
                }
            } else if (filename.includes(`${company}/${jobId}`)) {
                // Already has the full path structure
                filePath = filename;
            } else if (filename.startsWith(`${company}/`)) {
                // Has company but might be missing jobId
                filePath = filename;
            } else {
                // Just a filename or timestamp-based name, construct full path
                filePath = `${company}/${jobId}/${filename}`;
            }

            const { data, error } = await supabase.storage
                .from(bucketName)
                .download(filePath);

            if (error) throw error;

            // Create a download link
            const url = window.URL.createObjectURL(data);
            const link = document.createElement('a');
            link.href = url;
            link.download = originalName || filePath.split('/').pop();
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error downloading file:', err);
            alert('Failed to download file: ' + err.message);
        }
    };

    const toggleFullscreen = () => {
        setIsFullscreen(!isFullscreen);
    };

    const handleZoomIn = () => {
        setZoom(prev => Math.min(prev + 25, 300));
    };

    const handleZoomOut = () => {
        setZoom(prev => Math.max(prev - 25, 50));
    };

    const handleRotate = () => {
        setRotation(prev => (prev + 90) % 360);
    };

    const handlePrevFile = () => {
        if (currentIndex > 0) {
            onNavigate(currentIndex - 1);
        }
    };

    const handleNextFile = () => {
        if (currentIndex < files.length - 1) {
            onNavigate(currentIndex + 1);
        }
    };

    const getFileIcon = (category) => {
        switch (category) {
            case 'image':
                return <FiImage className="w-8 h-8" />;
            case 'pdf':
                return <FiFileText className="w-8 h-8" />;
            case 'cad':
                return <FiFile className="w-8 h-8" />;
            case 'audio':
                return <FiMusic className="w-8 h-8" />;
            case 'video':
                return <FiVideo className="w-8 h-8" />;
            default:
                return <FiFile className="w-8 h-8" />;
        }
    };

    const renderFileContent = () => {
        if (loading) {
            return (
                <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                        <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                        <p className="text-gray-600">Loading file...</p>
                    </div>
                </div>
            );
        }

        if (error) {
            return (
                <div className="flex items-center justify-center h-full">
                    <div className="text-center text-red-500">
                        <FiAlertCircle className="w-16 h-16 mx-auto mb-4" />
                        <p className="text-lg font-medium mb-2">Error Loading File</p>
                        <p className="text-sm text-gray-600">{error}</p>
                        <button
                            onClick={fetchFileUrl}
                            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                        >
                            Retry
                        </button>
                    </div>
                </div>
            );
        }

        if (!metadata || !fileUrl) return null;

        // Render based on file category
        switch (metadata.category) {
            case 'image':
                return (
                    <div className="flex items-center justify-center h-full overflow-auto p-4">
                        <img
                            src={fileUrl}
                            alt={originalName}
                            style={{
                                transform: `scale(${zoom / 100}) rotate(${rotation}deg)`,
                                transition: 'transform 0.3s ease',
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain'
                            }}
                            className="shadow-2xl"
                            onError={(e) => {
                                console.error('Image load error');
                                setError('Failed to load image');
                            }}
                        />
                    </div>
                );

            case 'pdf':
                return (
                    <div className="h-full w-full">
                        <iframe
                            src={fileUrl}
                            className="w-full h-full border-0"
                            title={originalName}
                            onError={(e) => {
                                console.error('PDF load error');
                                setError('Failed to load PDF');
                            }}
                        />
                    </div>
                );

            case 'text':
                return (
                    <div className="h-full w-full overflow-auto bg-white p-8">
                        <div className="max-w-4xl mx-auto">
                            <pre className="whitespace-pre-wrap font-mono text-sm text-gray-800 bg-gray-50 p-6 rounded-lg border border-gray-200">
                                {fileContent || 'Loading...'}
                            </pre>
                        </div>
                    </div>
                );

            case 'rtf':
                return (
                    <div className="h-full w-full overflow-auto bg-white p-8">
                        <div className="max-w-4xl mx-auto">
                            <div className="bg-white shadow-lg rounded-lg overflow-hidden">
                                <div className="border-b border-gray-200 bg-gray-50 p-4">
                                    <h3 className="text-sm font-semibold text-gray-700">RTF Document Preview</h3>
                                    <p className="text-xs text-gray-500 mt-0.5">Rich text formatting is not rendered — plain text shown</p>
                                </div>
                                <div className="p-6">
                                    <pre className="whitespace-pre-wrap font-sans text-sm text-gray-800 leading-relaxed">
                                        {fileContent || 'Loading...'}
                                    </pre>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'cad':
                return (
                    <div className="h-full w-full bg-gray-100">
                        <div
                            ref={cadContainerRef}
                            className="w-full h-full"
                            style={{ minHeight: '600px' }}
                        >
                            {!cadViewer && !error && (
                                <div className="flex items-center justify-center h-full">
                                    <div className="text-center">
                                        <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                                        <p className="text-gray-600">Loading CAD drawing...</p>
                                    </div>
                                </div>
                            )}
                            {error === 'DWG_NOT_SUPPORTED' && (
                                <div className="flex items-center justify-center h-full p-8">
                                    <div className="text-center bg-white rounded-2xl shadow-xl p-12 max-w-md">
                                        <div className="w-24 h-24 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white">
                                            <FiAlertCircle className="w-12 h-12" />
                                        </div>
                                        <h3 className="text-2xl font-bold text-gray-900 mb-2">
                                            DWG Format Not Supported
                                        </h3>
                                        <p className="text-gray-600 mb-6">
                                            DWG files require conversion to DXF format for browser preview.
                                        </p>
                                        <div className="space-y-3">
                                            <button
                                                onClick={handleDownload}
                                                className="w-full py-3 px-6 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all shadow-md hover:shadow-lg font-medium flex items-center justify-center gap-2"
                                            >
                                                <FiDownload className="w-5 h-5" />
                                                Download DWG File
                                            </button>
                                            <p className="text-sm text-gray-500">
                                                Convert to DXF using AutoCAD, LibreCAD, or online converters
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                );

            case 'html':
                return (
                    <div className="h-full w-full overflow-auto bg-white p-8">
                        <div className="max-w-full mx-auto">
                            <div className="bg-white shadow-lg rounded-lg overflow-hidden">
                                {/* Preview Mode - Rendered HTML */}
                                <div className="border-b border-gray-200 bg-gray-50 p-4">
                                    <h3 className="text-sm font-semibold text-gray-700">HTML Preview</h3>
                                </div>
                                <div className="p-6">
                                    <iframe
                                        srcDoc={fileContent || '<p>Loading...</p>'}
                                        className="w-full h-[600px] border border-gray-200 rounded"
                                        title="HTML Preview"
                                        sandbox="allow-same-origin"
                                    />
                                </div>
                                {/* Source Code View */}
                                <details className="border-t border-gray-200">
                                    <summary className="cursor-pointer bg-gray-50 p-4 text-sm font-semibold text-gray-700 hover:bg-gray-100">
                                        View Source Code
                                    </summary>
                                    <div className="p-4 bg-gray-900">
                                        <pre className="whitespace-pre-wrap font-mono text-xs text-green-400 overflow-auto">
                                            {fileContent || 'Loading...'}
                                        </pre>
                                    </div>
                                </details>
                            </div>
                        </div>
                    </div>
                );

            case 'word':
                return (
                    <div className="h-full w-full overflow-hidden bg-gray-100">
                        <iframe
                            src={`https://docs.google.com/viewer?url=${encodeURIComponent(fileUrl)}&embedded=true`}
                            className="w-full h-full border-0"
                            title="Google Docs Viewer"
                            sandbox="allow-scripts allow-same-origin"
                        />
                    </div>
                );


            case 'excel': {
                const excelCss = `
                    body { margin: 0; font-family: Calibri, Arial, sans-serif; font-size: 12px; background: #fff; }
                    table { border-collapse: collapse; width: max-content; }
                    td, th { border: 1px solid #d0d7de; padding: 3px 8px; white-space: nowrap; vertical-align: middle; min-width: 64px; }
                    tr:first-child td { background: #f0f2f5; font-weight: 600; position: sticky; top: 0; z-index: 1; }
                    tr:nth-child(even) td { background: #f8f9fb; }
                    tr:nth-child(odd):not(:first-child) td { background: #fff; }
                    tr:hover td { background: #e8f0fe !important; }
                `;
                const currentSheetHtml = excelData?.sheets?.[excelData.sheetNames[excelData.currentSheet]] || '';
                const srcDoc = `<!DOCTYPE html><html><head><style>${excelCss}</style></head><body>${currentSheetHtml}</body></html>`;
                return (
                    <div className="h-full w-full flex flex-col bg-white">
                        {excelData ? (
                            <>
                                {excelData.sheetNames.length > 1 && (
                                    <div className="flex gap-1 px-4 pt-2 border-b border-gray-200 bg-gray-50 flex-shrink-0">
                                        {excelData.sheetNames.map((name, idx) => (
                                            <button
                                                key={idx}
                                                onClick={() => setExcelData(prev => ({ ...prev, currentSheet: idx }))}
                                                className={`px-3 py-1.5 text-xs font-medium rounded-t border ${excelData.currentSheet === idx ? 'bg-white border-gray-300 border-b-white text-gray-900' : 'bg-gray-100 border-transparent text-gray-500 hover:text-gray-700'}`}
                                            >
                                                {name}
                                            </button>
                                        ))}
                                    </div>
                                )}
                                <iframe
                                    srcDoc={srcDoc}
                                    className="flex-1 border-0 w-full"
                                    title="Excel Preview"
                                    sandbox="allow-same-origin"
                                />
                            </>
                        ) : (
                            <div className="flex items-center justify-center h-full">
                                <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
                            </div>
                        )}
                    </div>
                );
            }

            case 'eml':
                return (
                    <div className="h-full w-full overflow-auto bg-white p-8">
                        <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-lg overflow-hidden">
                            {parsedEmail ? (
                                <div>
                                    {/* Email Headers */}
                                    <div className="bg-gray-50 border-b border-gray-200 p-6">
                                        <div className="mb-3">
                                            <span className="text-xs font-semibold text-gray-500 uppercase">From:</span>
                                            <p className="text-sm text-gray-900 mt-1">{parsedEmail.from?.name || parsedEmail.from?.address || 'Unknown'}</p>
                                        </div>
                                        <div className="mb-3">
                                            <span className="text-xs font-semibold text-gray-500 uppercase">To:</span>
                                            <p className="text-sm text-gray-900 mt-1">
                                                {parsedEmail.to?.map(t => t.name || t.address).join(', ') || 'Unknown'}
                                            </p>
                                        </div>
                                        <div className="mb-3">
                                            <span className="text-xs font-semibold text-gray-500 uppercase">Subject:</span>
                                            <p className="text-sm font-semibold text-gray-900 mt-1">{parsedEmail.subject || 'No Subject'}</p>
                                        </div>
                                        <div>
                                            <span className="text-xs font-semibold text-gray-500 uppercase">Date:</span>
                                            <p className="text-sm text-gray-900 mt-1">
                                                {parsedEmail.date ? new Date(parsedEmail.date).toLocaleString() : 'Unknown'}
                                            </p>
                                        </div>
                                    </div>
                                    {/* Email Body */}
                                    <div className="p-6">
                                        {parsedEmail.html ? (
                                            <div
                                                className="prose prose-sm max-w-none"
                                                dangerouslySetInnerHTML={{ __html: parsedEmail.html }}
                                            />
                                        ) : (
                                            <pre className="whitespace-pre-wrap text-sm text-gray-800">
                                                {parsedEmail.text || 'No content'}
                                            </pre>
                                        )}
                                    </div>
                                    {/* Attachments */}
                                    {parsedEmail.attachments && parsedEmail.attachments.length > 0 && (
                                        <div className="border-t border-gray-200 p-6 bg-gray-50">
                                            <span className="text-xs font-semibold text-gray-500 uppercase">Attachments:</span>
                                            <div className="mt-2 space-y-2">
                                                {parsedEmail.attachments.map((att, idx) => (
                                                    <div key={idx} className="text-sm text-gray-700 flex items-center gap-2">
                                                        <FiFile className="w-4 h-4" />
                                                        {att.filename || `Attachment ${idx + 1}`} ({att.mimeType})
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <p className="text-center text-gray-500 p-6">Loading email...</p>
                            )}
                        </div>
                    </div>
                );

            case 'document':
                return (
                    <div className="flex flex-col items-center justify-center h-full p-8">
                        <div className="text-center bg-white rounded-2xl shadow-xl p-12 max-w-md">
                            <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white">
                                {getFileIcon(metadata.category)}
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-2">
                                {originalName}
                            </h3>
                            <p className="text-gray-600 mb-6">
                                {metadata.sizeFormatted} • {metadata.extension.toUpperCase()}
                            </p>
                            <div className="space-y-3">
                                <button
                                    onClick={handleDownload}
                                    className="w-full py-3 px-6 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all shadow-md hover:shadow-lg font-medium flex items-center justify-center gap-2"
                                >
                                    <FiDownload className="w-5 h-5" />
                                    Download File
                                </button>
                                <p className="text-sm text-gray-500">
                                    This file type cannot be previewed in the browser
                                </p>
                            </div>
                        </div>
                    </div>
                );

            case 'audio':
                return (
                    <div className="flex items-center justify-center h-full p-6">
                        <div className="w-full max-w-xl bg-white rounded-2xl shadow-2xl p-8 text-center">
                            <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-5 text-white">
                                <FiMusic className="w-10 h-10" />
                            </div>

                            <h3 className="text-xl font-bold text-gray-900 mb-1 truncate">
                                {originalName || metadata.name}
                            </h3>

                            <p className="text-sm text-gray-500 mb-6">
                                {metadata.sizeFormatted} • {metadata.extension.toUpperCase()}
                            </p>

                            <audio
                                controls
                                src={fileUrl}
                                className="w-full"
                                preload="metadata"
                                onError={() => setError('Failed to load audio file')}
                            >
                                Your browser does not support the audio player.
                            </audio>
                        </div>
                    </div>
                );

            case 'video':
                return (
                    <div className="flex items-center justify-center h-full p-4 bg-black">
                        <video
                            controls
                            src={fileUrl}
                            className="max-w-full max-h-full rounded-xl shadow-2xl bg-black"
                            preload="metadata"
                            onError={() => setError('Failed to load video file')}
                        >
                            Your browser does not support the video player.
                        </video>
                    </div>
                );

            default:
                return (
                    <div className="flex items-center justify-center h-full">
                        <div className="text-center">
                            <FiFile className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                            <p className="text-gray-600 mb-4">File preview not available</p>
                            <button
                                onClick={handleDownload}
                                className="py-2 px-6 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium flex items-center justify-center gap-2 mx-auto"
                            >
                                <FiDownload className="w-4 h-4" />
                                Download File
                            </button>
                        </div>
                    </div>
                );
        }
    };

    // Render CAD viewer when container is ready
    useEffect(() => {
        if (cadContainerRef.current && fileUrl && metadata?.category === 'cad' && fileContent === 'CAD_MODEL' && !cadViewer && currentFilePath) {
            const initCadViewer = async () => {
                try {
                    const container = cadContainerRef.current;
                    const canvasWidth = container.clientWidth;
                    const canvasHeight = container.clientHeight || 600;

                    // Check file extension - dxf-viewer only supports DXF files well
                    const extension = currentFilePath.split('.').pop()?.toLowerCase();

                    if (extension === 'dwg') {
                        // DWG files are not fully supported by dxf-viewer
                        // Set error flag but don't stop loading - show custom UI
                        setError('DWG_NOT_SUPPORTED');
                        setLoading(false);
                        return;
                    }

                    const viewer = new DxfViewer(container, {
                        canvasWidth,
                        canvasHeight,
                        autoResize: true,
                        clearColor: { r: 240, g: 240, b: 240 }
                    });

                    await viewer.Load({
                        url: fileUrl,
                        fonts: []
                    });

                    setCadViewer(viewer);
                } catch (error) {
                    console.error('Error loading CAD file:', error);
                    setError('Failed to load CAD file: ' + error.message);
                }
            };

            initCadViewer();
        }

        return () => {
            if (cadViewer) {
                cadViewer.Destroy();
            }
        };
    }, [cadContainerRef.current, fileUrl, metadata, fileContent, currentFilePath]);

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e) => {
            switch (e.key) {
                case 'Escape':
                    onClose();
                    break;
                case 'ArrowLeft':
                    handlePrevFile();
                    break;
                case 'ArrowRight':
                    handleNextFile();
                    break;
                case '+':
                case '=':
                    if (metadata?.category === 'image') handleZoomIn();
                    break;
                case '-':
                case '_':
                    if (metadata?.category === 'image') handleZoomOut();
                    break;
                default:
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [metadata, currentIndex, files.length]);

    return createPortal(
        <div className={`mobile-file-viewer fixed inset-0 z-[11000] bg-black bg-opacity-95 flex flex-col !mt-0`}>
            {/* Header */}
            <div className="bg-zinc-900 border-b border-gray-800 px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-4 flex-1 min-w-0">
                    {/* Navigation arrows for multiple files */}
                    {files.length > 1 && (
                        <div className="flex items-center gap-2">
                            <button
                                onClick={handlePrevFile}
                                disabled={currentIndex === 0}
                                className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                title="Previous file"
                            >
                                <FiChevronLeft className="w-5 h-5" />
                            </button>
                            <span className="text-sm text-gray-400">
                                {currentIndex + 1} / {files.length}
                            </span>
                            <button
                                onClick={handleNextFile}
                                disabled={currentIndex === files.length - 1}
                                className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                title="Next file"
                            >
                                <FiChevronRight className="w-5 h-5" />
                            </button>
                        </div>
                    )}

                    <div className="flex-1 min-w-0">
                        <h2 className="text-white font-semibold text-lg truncate">
                            {originalName || filename.split('/').pop()}
                        </h2>
                        {metadata && (
                            <p className="text-gray-400 text-sm">
                                {metadata.sizeFormatted} • {metadata.extension.toUpperCase()}
                            </p>
                        )}
                    </div>
                </div>

                {/* Controls */}
                <div className="flex items-center gap-2 ml-4">
                    {/* Image-specific controls */}
                    {metadata?.category === 'image' && (
                        <>
                            <button
                                onClick={handleZoomOut}
                                className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
                                title="Zoom out"
                            >
                                <FiZoomOut className="w-5 h-5" />
                            </button>
                            <span className="text-gray-400 text-sm min-w-[60px] text-center">
                                {zoom}%
                            </span>
                            <button
                                onClick={handleZoomIn}
                                className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
                                title="Zoom in"
                            >
                                <FiZoomIn className="w-5 h-5" />
                            </button>
                            <button
                                onClick={handleRotate}
                                className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
                                title="Rotate"
                            >
                                <FiRotateCw className="w-5 h-5" />
                            </button>
                            <div className="w-px h-6 bg-gray-700 mx-2"></div>
                        </>
                    )}

                    <button
                        onClick={handleDownload}
                        className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
                        title="Download"
                    >
                        <FiDownload className="w-5 h-5" />
                    </button>

                    <div className="w-px h-6 bg-gray-700 mx-2"></div>

                    <button
                        onClick={onClose}
                        className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
                        title="Close"
                    >
                        <FiX className="w-5 h-5" />
                    </button>
                </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
                {renderFileContent()}
            </div>

            {/* Keyboard shortcuts hint */}
            <div className="bg-gray-900 border-t border-gray-800 px-6 py-2">
                <div className="flex items-center justify-center gap-6 text-xs text-gray-500">
                    <span><kbd className="px-2 py-1 bg-gray-800 rounded">ESC</kbd> Close</span>
                    {files.length > 1 && (
                        <>
                            <span><kbd className="px-2 py-1 bg-gray-800 rounded">←</kbd> Previous</span>
                            <span><kbd className="px-2 py-1 bg-gray-800 rounded">→</kbd> Next</span>
                        </>
                    )}
                    {metadata?.category === 'image' && (
                        <>
                            <span><kbd className="px-2 py-1 bg-gray-800 rounded">+</kbd> Zoom in</span>
                            <span><kbd className="px-2 py-1 bg-gray-800 rounded">-</kbd> Zoom out</span>
                        </>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default FileViewer;